import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Search, Plus, Eye, EyeOff, Trash2, Edit3, Sparkles, Save, X, Upload, MapPin, LogOut, BarChart3 } from "lucide-react";

import { API } from "./utils/constants";

const CAT_OPTIONS = [
  { value: "culture", label: "CULTURE", color: "var(--cat-culture-solid)" },
  { value: "food", label: "FOODIE", color: "var(--cat-foodie-solid)" },
  { value: "nightlife", label: "NIGHTLIFE", color: "var(--cat-nightlife-solid)" },
  { value: "special", label: "SPECIAL", color: "var(--cat-special-solid)" },
  { value: "popular", label: "POPULAR", color: "var(--cat-popular-solid)" },
  { value: "hidden_gem", label: "HIDDEN", color: "var(--cat-hidden-solid)" },
];
const CAT_LABEL_MAP = { culture:"CULTURE", food:"FOODIE", nightlife:"NIGHTLIFE", special:"SPECIAL", popular:"POPULAR", hidden_gem:"HIDDEN" };
const CITIES = ["Tokyo", "Kyoto", "Osaka"];

// ===== LOGIN GATE =====
const LoginGate = ({ onLogin }) => {
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pwd });
      onLogin(res.data.token);
    } catch (error) {
      setError(error?.response?.status === 401 ? "Wrong password" : "Could not reach the sign-in service. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--zen-bg)" }}>
      <form onSubmit={submit} className="p-8 rounded-2xl w-full max-w-sm" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)" }}>
        <h1 className="font-heading text-2xl mb-1" style={{ color: "var(--zen-text)" }}>Japan Unfolded Creator</h1>
        <p className="text-sm mb-6" style={{ color: "var(--zen-sub)" }}>Enter your password to manage the catalog</p>
        <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} placeholder="Password"
          className="zen-search mb-3" style={{ paddingLeft: 14 }} data-testid="admin-password" autoFocus />
        {error && <p className="text-xs mb-2" style={{ color: "var(--cat-nightlife-solid)" }}>{error}</p>}
        <button type="submit" disabled={loading} className="zen-btn zen-btn-primary w-full" data-testid="admin-login-btn">
          {loading ? "..." : "Enter"}
        </button>
      </form>
    </div>
  );
};

// ===== WIKI IMAGE =====
const WikiThumb = ({ wiki, image, size = 80 }) => {
  const [src, setSrc] = useState(image || "");
  useEffect(() => {
    if (image) { setSrc(image); return; }
    if (!wiki) return;
    fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(wiki)}`)
      .then(r => r.json()).then(d => { if (d.thumbnail) setSrc(d.thumbnail.source); }).catch(() => {});
  }, [wiki, image]);
  return (
    <div className="rounded-lg overflow-hidden shrink-0" style={{ width: size, height: size, background: "var(--zen-s2)" }}>
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><MapPin size={20} style={{ color: "var(--zen-dim)" }} /></div>}
    </div>
  );
};

// ===== CREATE / EDIT MODAL =====
const PlaceModal = ({ token, place, onSave, onClose }) => {
  const isEdit = !!place?.place_id;
  const reviewed = !!place?._sourcebook;
  const [saveError, setSaveError] = useState("");
  const [form, setForm] = useState({
    name: "", title: "", description: "", city: "Tokyo", category: "culture",
    address: "", neighborhood: "", duration: 1.5, duration_text: "1-2 hrs",
    wiki: "", image: "", ...(place || {}),
    // On a renewed card, "Description" is the opening text travellers read.
    ...(reviewed ? { description: place.description_short || "" } : {}),
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const aiAssist = async () => {
    if (!form.name) return;
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/admin/ai-assist`, { token, name: form.name, city: form.city });
      // a draft fills what is still empty; what the owner already typed is never overwritten
      const draft = Object.fromEntries(Object.entries(res.data || {}).filter(([, v]) => v !== "" && v != null));
      setForm(prev => ({ ...draft, ...Object.fromEntries(Object.entries(prev).filter(([, v]) => v !== "" && v != null)) }));
    } catch (e) { alert(e.response?.data?.detail || "The assistant couldn't draft this card — nothing was changed."); }
    setAiLoading(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("token", token);
      const res = await axios.post(`${API}/admin/upload-image`, fd);
      // Uploads are served by the API host, not by the site that serves the pages.
      set("image", /^https?:\/\//.test(res.data.url) ? res.data.url : `${API.replace(/\/api$/, "")}${res.data.url}`);
    } catch (e) { alert(`The photo didn't upload — ${e.response?.data?.detail || "try again"}`); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.name || !form.city) return;
    setSaving(true);
    setSaveError("");
    try {
      const payload = { ...form, token, display_category: CAT_LABEL_MAP[form.category] || "CULTURE" };
      if (reviewed) {
        payload.description_short = form.description;
        payload.description = place.description;
      }
      if (isEdit) {
        await axios.put(`${API}/admin/catalog/${place.place_id}`, payload);
      } else {
        await axios.post(`${API}/admin/catalog`, payload);
      }
      onSave();
    } catch (e) { setSaveError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not save this card. Please try again."); }
    setSaving(false);
  };

  const inputStyle = { background: "var(--zen-s2)", border: "1px solid var(--zen-border)", color: "var(--zen-text)", borderRadius: 8, padding: "8px 12px", width: "100%", fontSize: 13, outline: "none" };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "var(--zen-sub)", marginBottom: 4, display: "block" };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4" style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="w-full max-w-2xl rounded-2xl overflow-y-auto" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)", maxHeight: "85vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5" style={{ borderBottom: "1px solid var(--zen-border)" }}>
          <h2 className="font-heading text-lg" style={{ color: "var(--zen-text)" }}>{isEdit ? "Edit Place" : "Create New Place"}</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:opacity-70"><X size={18} style={{ color: "var(--zen-dim)" }} /></button>
        </div>

        <div className="p-5 space-y-4">
          {reviewed && <div className="text-sm p-3 rounded-lg" style={{ background: "var(--zen-s2)" }}>
            <strong>Renewed card</strong><p className="mt-1">This card is built from the reviewed research. Anything you change here is kept as your edit, and it is what travellers and the planner will read.</p>
            <details className="mt-2"><summary className="cursor-pointer">Reviewed research behind this card</summary><pre className="text-xs mt-2 whitespace-pre-wrap break-words max-h-64 overflow-y-auto">{JSON.stringify(place._sourcebook, null, 2)}</pre></details>
          </div>}
          {saveError && <p role="alert" className="text-sm">{saveError}</p>}
          <fieldset className="space-y-4">
          {/* Row 1: Name + AI assist */}
          <div>
            <label style={labelStyle}>Place Name</label>
            <div className="flex gap-2">
              <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Nezu Museum" style={inputStyle} data-testid="modal-name" />
              <select value={form.city} onChange={e => set("city", e.target.value)} style={{ ...inputStyle, width: 120 }}>
                {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button onClick={aiAssist} disabled={aiLoading || !form.name}
                className="zen-btn zen-btn-primary shrink-0 px-4 py-2 text-xs" data-testid="ai-assist-btn">
                {aiLoading ? "..." : <><Sparkles size={14} /> AI Fill</>}
              </button>
            </div>
          </div>

          {/* Row 2: Title */}
          <div>
            <label style={labelStyle}>Punch Line</label>
            <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="A catchy one-liner" style={inputStyle} data-testid="modal-title" />
          </div>

          {/* Row 3: Description */}
          <div>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder="2-3 vivid sentences" rows={3} style={{ ...inputStyle, resize: "vertical" }} data-testid="modal-desc" />
          </div>

          {/* The card's front and back, as travellers read them (2026-10-17) */}
          <div>
            <label style={labelStyle}>Front line — what it is (shown first on the card)</label>
            <textarea value={form.micro || ""} onChange={e => set("micro", e.target.value)} placeholder="One sharp sentence, 15–25 words" rows={2} style={{ ...inputStyle, resize: "vertical" }} data-testid="modal-micro" />
          </div>
          <div>
            <label style={labelStyle}>Good for — who it serves</label>
            <textarea value={form.good_for || ""} onChange={e => set("good_for", e.target.value)} placeholder="Who loves it, and who it is not for" rows={2} style={{ ...inputStyle, resize: "vertical" }} data-testid="modal-good-for" />
          </div>
          <div>
            <label style={labelStyle}>Why we chose it</label>
            <textarea value={form.why_chosen || ""} onChange={e => set("why_chosen", e.target.value)} placeholder="Why this place over the obvious one" rows={2} style={{ ...inputStyle, resize: "vertical" }} data-testid="modal-why-chosen" />
          </div>

          {/* Row 4: Category + Duration */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label style={labelStyle}>Category</label>
              <select value={form.category} onChange={e => { set("category", e.target.value); set("display_category", CAT_LABEL_MAP[e.target.value]); }} style={inputStyle} data-testid="modal-category">
                {CAT_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Duration (hrs)</label>
              <input type="number" step="0.5" min="0.5" max="12" value={form.duration} onChange={e => set("duration", parseFloat(e.target.value) || 1.5)} style={inputStyle} data-testid="modal-duration" />
            </div>
            <div>
              <label style={labelStyle}>Duration Text</label>
              <input value={form.duration_text} onChange={e => set("duration_text", e.target.value)} placeholder="1-2 hrs" style={inputStyle} />
            </div>
          </div>

          {/* Row 5: Address + Neighborhood */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={labelStyle}>Address</label>
              <input value={form.address} onChange={e => set("address", e.target.value)} placeholder="Full address" style={inputStyle} data-testid="modal-address" />
            </div>
            <div>
              <label style={labelStyle}>Neighborhood</label>
              <input value={form.neighborhood} onChange={e => set("neighborhood", e.target.value)} placeholder="e.g. Shibuya" style={inputStyle} />
            </div>
          </div>

          </fieldset>
          {/* Row 6: Image */}
          <div>
            <label style={labelStyle}>Image</label>
            <div className="flex gap-3 items-center">
              <WikiThumb wiki={form.wiki} image={form.image} size={64} />
              <div className="flex-1 space-y-2">
                <input value={form.image} onChange={e => set("image", e.target.value)} placeholder="Paste image URL or upload below" style={inputStyle} data-testid="modal-image-url" />
                <label className="zen-btn zen-btn-outline text-xs px-3 py-1.5 cursor-pointer inline-flex">
                  <Upload size={12} /> {uploading ? "Uploading..." : "Upload photo"}
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
              </div>
            </div>
          </div>

          {/* Row 7: Wiki */}
          <div>
            <label style={labelStyle}>Wikipedia Article (for fallback image)</label>
            <input value={form.wiki} onChange={e => set("wiki", e.target.value)} placeholder="e.g. Senso-ji" style={inputStyle} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5" style={{ borderTop: "1px solid var(--zen-border)" }}>
          <button onClick={onClose} className="zen-btn zen-btn-outline px-5 py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={saving || !form.name} className="zen-btn zen-btn-primary px-6 py-2 text-sm" data-testid="modal-save-btn">
            {saving ? "Saving..." : <><Save size={14} /> {isEdit ? "Update" : "Create"}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== FUNNEL (backend core/funnel): how many visits reached each step, and where they left =====
const STEP_LABELS = {
  landing: "Landing", dates: "Dates", cities: "Cities", traveler: "Who travels", style: "Rhythm", vibes: "Vibes",
  practical: "Good to know", input: "Wishlist", summary: "Selection", catalog: "Catalog", result: "Trip",
  preview_locked: "Free preview shown", payment_open: "Plans opened",
};
const FunnelPanel = ({ token }) => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/funnel`, { params: { token, days } }).then(r => setData(r.data)).catch(() => setData({ error: true }));
  }, [open, days, token]);
  return (
    <div className="mb-5 p-4 rounded-xl" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)" }} data-testid="admin-funnel">
      <button onClick={() => setOpen(!open)} className="text-sm font-semibold" style={{ background: "none", border: 0, color: "var(--zen-text)", cursor: "pointer" }}>
        {open ? "▾" : "▸"} Funnel — where people leave
      </button>
      {open && (
        <div className="mt-3">
          <div className="flex gap-2 mb-3">
            {[1, 7, 30].map(d => (
              <button key={d} onClick={() => setDays(d)} className="text-xs px-3 py-1 rounded-full"
                style={{ border: "1px solid var(--zen-border)", background: days === d ? "var(--zen-text)" : "transparent", color: days === d ? "var(--zen-bg)" : "var(--zen-sub)" }}>
                {d === 1 ? "Today" : `${d} days`}
              </button>
            ))}
          </div>
          {!data ? <p className="text-xs">Loading…</p> : data.error ? <p className="text-xs">The funnel could not be loaded.</p> : (
            <table className="text-xs w-full">
              <thead><tr style={{ color: "var(--zen-dim)" }}><th className="text-left py-1">Step</th><th className="text-right">Visits</th><th className="text-right">Of landing</th><th className="text-right">Went on</th></tr></thead>
              <tbody>
                {data.steps.map(s => (
                  <tr key={s.step} style={{ borderTop: "1px solid var(--zen-border)" }}>
                    <td className="py-1">{STEP_LABELS[s.step] || s.step}</td>
                    <td className="text-right">{s.visits}</td>
                    <td className="text-right">{s.of_landing == null ? "—" : `${Math.round(s.of_landing * 100)}%`}</td>
                    <td className="text-right">{s.of_previous == null ? "—" : `${Math.round(s.of_previous * 100)}%`}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: "2px solid var(--zen-border)" }}><td className="py-1 font-semibold">Payments</td><td className="text-right font-semibold">{data.payments}</td><td /><td /></tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// ===== USERS (backend core/users): each traveller, how far they went, what they paid and built =====
const UsersPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/users`, { params: { token } }).then(r => setData(r.data)).catch(() => setData({ error: true }));
  }, [open, token]);
  const day = iso => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—");
  const trip = t => (t?.cities ? Object.entries(t.cities).map(([c, d]) => `${c} ${d}`).join(" · ") : "—");
  const host = url => { try { return url ? new URL(url).hostname : "—"; } catch { return "—"; } };
  return (
    <div className="mb-5 p-4 rounded-xl" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)" }} data-testid="admin-users">
      <button onClick={() => setOpen(!open)} className="text-sm font-semibold" style={{ background: "none", border: 0, color: "var(--zen-text)", cursor: "pointer" }}>
        {open ? "▾" : "▸"} Travellers — who they are and how far they go
      </button>
      {open && (
        <div className="mt-3" style={{ overflowX: "auto" }}>
          {!data ? <p className="text-xs">Loading…</p> : data.error ? <p className="text-xs">The travellers could not be loaded.</p>
            : !data.users.length ? <p className="text-xs">Nobody yet.</p> : (
            <table className="text-xs w-full">
              <thead><tr style={{ color: "var(--zen-dim)" }}>
                <th className="text-left py-1">Email</th><th className="text-left">First</th><th className="text-left">Last</th>
                <th className="text-left">Furthest step</th><th className="text-left">Trip</th><th className="text-left">Came from</th>
                <th className="text-right">Paid</th><th className="text-right">Trips</th>
              </tr></thead>
              <tbody>
                {data.users.map(u => (
                  <tr key={u.email} style={{ borderTop: "1px solid var(--zen-border)" }}>
                    <td className="py-1">{u.email}</td><td>{day(u.first_seen)}</td><td>{day(u.last_seen)}</td>
                    <td>{STEP_LABELS[u.furthest] || u.furthest || "—"}</td><td>{trip(u.trip)}</td>
                    <td>{u.source?.utm_source || host(u.source?.referrer)}</td>
                    <td className="text-right">{u.payments || 0}</td><td className="text-right">{u.saved_trips || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// ===== FEEDBACK (backend core/feedback): what travellers told us, newest first =====
const FeedbackPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/feedback`, { params: { token } }).then(r => setData(r.data)).catch(() => setData({ error: true }));
  }, [open, token]);
  return (
    <div className="mb-5 p-4 rounded-xl" style={{ background: "var(--zen-s1)", border: "1px solid var(--zen-border)" }} data-testid="admin-feedback">
      <button onClick={() => setOpen(!open)} className="text-sm font-semibold" style={{ background: "none", border: 0, color: "var(--zen-text)", cursor: "pointer" }}>
        {open ? "▾" : "▸"} Feedback — what travellers told us
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {!data ? <p className="text-xs">Loading…</p> : data.error ? <p className="text-xs">The feedback could not be loaded.</p>
            : !data.feedback.length ? <p className="text-xs">Nothing yet.</p> : data.feedback.map((f, i) => (
              <div key={i} className="text-xs p-2" style={{ borderTop: i ? "1px solid var(--zen-border)" : 0 }}>
                <div style={{ color: "var(--zen-dim)" }}>{new Date(f.at).toLocaleString("en-GB")} · {f.email || "not signed in"} · {f.context?.page || ""}</div>
                <div style={{ whiteSpace: "pre-wrap", color: "var(--zen-text)", marginTop: 2 }}>{f.message}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

// ===== ADMIN DASHBOARD =====
const AdminDashboard = ({ token, onLogout }) => {
  const [places, setPlaces] = useState([]);
  const [stats, setStats] = useState({});
  const [filterCity, setFilterCity] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // null | "create" | place object
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const requestVersion = useRef(0);

  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setLoadError("");
    try {
      const params = new URLSearchParams({ token });
      if (filterCity) params.append("city", filterCity);
      if (filterCat) params.append("category", filterCat);
      if (search) params.append("search", search);
      const res = await axios.get(`${API}/admin/catalog?${params}`);
      if (version !== requestVersion.current) return;
      setPlaces(res.data.places);
      setStats(res.data.stats);
    } catch (e) {
      if (version !== requestVersion.current) return;
      if (e?.response?.status === 401) onLogout();
      else setLoadError("The catalog could not be loaded. Please retry.");
    }
    if (version === requestVersion.current) setLoading(false);
  }, [token, filterCity, filterCat, search, onLogout]);

  useEffect(() => { load(); }, [load]);

  const toggleVisibility = async (p) => {
    try {
      await axios.patch(`${API}/admin/catalog/${p.place_id}/visibility`, { token });
      load();
    } catch (e) { alert(`The card's visibility didn't change — ${e.response?.data?.detail || "try again"}`); }
  };

  const deletePlace = async (p) => {
    if (!window.confirm(p._sourcebook ? `Hide "${p.name}" from the catalog? Its research will be kept.` : `Delete "${p.name}" permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/catalog/${p.place_id}?token=${token}`);
      load();
    } catch (e) { alert(`The card wasn't removed — ${e.response?.data?.detail || "try again"}`); }
  };

  const badgeColor = (cat) => {
    const opt = CAT_OPTIONS.find(c => c.value === cat);
    return opt?.color || "var(--zen-dim)";
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--zen-bg)" }}>
      {/* Top bar */}
      <div className="sticky top-0 z-40 px-4 py-3 flex flex-wrap gap-3 items-center justify-between" style={{ background: "var(--zen-s1)", borderBottom: "1px solid var(--zen-border)" }}>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-heading text-lg" style={{ color: "var(--zen-text)" }}>Japan Unfolded Creator</h1>
          <div className="flex flex-wrap items-center gap-2 text-xs" style={{ color: "var(--zen-dim)" }}>
            <BarChart3 size={13} />
            <span>{stats.visible || 0} available for planning</span>
            <span>·</span>
            <span>{stats.hidden || 0} hidden</span>
            <span>· {stats.not_plannable || 0} information/review records</span>
            <span>·</span>
            <span>{stats.total || 0} total</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setModal("create")} className="zen-btn zen-btn-primary text-xs px-4 py-2" data-testid="create-place-btn">
            <Plus size={14} /> New Place
          </button>
          <button onClick={onLogout} className="zen-btn zen-btn-outline text-xs px-3 py-2" data-testid="admin-logout-btn">
            <LogOut size={14} />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        <FunnelPanel token={token} />
        <UsersPanel token={token} />
        <FeedbackPanel token={token} />
        {/* Filters */}
        <div className="flex gap-3 mb-4 flex-wrap items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2" size={14} style={{ color: "var(--zen-dim)" }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, description, address..."
              className="zen-search" data-testid="admin-search" />
          </div>
          <select value={filterCity} onChange={e => setFilterCity(e.target.value)} className="zen-search" style={{ width: 130, paddingLeft: 12 }} data-testid="admin-filter-city">
            <option value="">All cities</option>
            {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="zen-search" style={{ width: 140, paddingLeft: 12 }} data-testid="admin-filter-cat">
            <option value="">All categories</option>
            {CAT_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        {loadError && <p role="alert" className="text-sm mb-3">{loadError} <button onClick={load} className="underline">Retry</button></p>}
        {/* Results count */}
        <p className="text-xs mb-3" style={{ color: "var(--zen-dim)" }}>
          {loading ? "Loading..." : `${places.length} places`}
        </p>

        {/* Place list */}
        <div className="space-y-2">
          {places.map(p => (
            <div key={p.place_id} className="flex items-center gap-3 p-3 rounded-xl transition-all hover:shadow-md"
              style={{ background: p.visible === false ? "var(--zen-s2)" : "var(--zen-s1)", border: "1px solid var(--zen-border)", opacity: p.visible === false ? 0.6 : 1 }}
              data-testid={`admin-place-${p.place_id}`}>
              <WikiThumb wiki={p.wiki} image={p.image} size={56} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-sm" style={{ color: "var(--zen-text)" }}>{p.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase" style={{ background: badgeColor(p.category), color: "#fff" }}>
                    {CAT_LABEL_MAP[p.category] || p.category}
                  </span>
                  <span className="text-xs" style={{ color: "var(--zen-dim)" }}>{p.city}</span>
                  {p.visible === false && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: "var(--zen-dim)", color: "var(--zen-bg)" }}>HIDDEN</span>}
                </div>
                {p.title && <p className="text-xs truncate" style={{ color: "var(--zen-sub)" }}>{p.title}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => toggleVisibility(p)} className="p-2 rounded-lg hover:opacity-70" title={p.visible === false ? "Show" : "Hide"}
                  data-testid={`toggle-vis-${p.place_id}`}>
                  {p.visible === false ? <EyeOff size={15} style={{ color: "var(--zen-dim)" }} /> : <Eye size={15} style={{ color: "var(--zen-accent)" }} />}
                </button>
                <button onClick={() => setModal(p)} className="p-2 rounded-lg hover:opacity-70" title="Edit" data-testid={`edit-${p.place_id}`}>
                  <Edit3 size={15} style={{ color: "var(--zen-accent)" }} />
                </button>
                <button onClick={() => deletePlace(p)} className="p-2 rounded-lg hover:opacity-70" title={p._sourcebook ? "Archive" : "Delete"} data-testid={`delete-${p.place_id}`}>
                  <Trash2 size={15} style={{ color: "var(--cat-nightlife-solid)" }} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {places.length === 0 && !loading && (
          <p className="text-center py-16" style={{ color: "var(--zen-dim)" }}>No places match your filters.</p>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <PlaceModal
          token={token}
          place={modal === "create" ? null : modal}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
};

// ===== MAIN ADMIN PAGE =====
export default function AdminPanel() {
  const [token, setToken] = useState(() => sessionStorage.getItem("ju-admin-token") || "");

  const handleLogin = (t) => {
    setToken(t);
    sessionStorage.setItem("ju-admin-token", t);
  };

  const handleLogout = useCallback(() => {
    setToken("");
    sessionStorage.removeItem("ju-admin-token");
  }, []);

  if (!token) return <LoginGate onLogin={handleLogin} />;
  return <AdminDashboard token={token} onLogout={handleLogout} />;
}
